/**
 * I/O layer for the NOAA/NWS ArcGIS ImageServer radar source (RESEARCH.md
 * opcja B / DECISIONS.md D-001). Pure computation lives in frames.ts and
 * mercator.ts; this module only does fetch/Image loading and thin wrapping.
 */
import { boundsToMercatorBbox, type LonLatBounds } from "./mercator";

const IMAGE_SERVER_BASE =
  "https://mapservices.weather.noaa.gov/eventdriven/rest/services/radar/radar_base_reflectivity_time/ImageServer";

/** Continental US bounding box (lon/lat), per DECISIONS.md D-002 / builder brief. */
export const CONUS_BOUNDS: LonLatBounds = {
  west: -125.5,
  south: 23.5,
  east: -66,
  north: 50.5
};

/** Requested output width in px; height is derived to match the bbox aspect ratio. */
export const EXPORT_IMAGE_WIDTH = 2000;

export class RadarApiError extends Error {}

export interface TimeExtent {
  startMs: number;
  endMs: number;
}

/** Fetch the current time extent from the ImageServer metadata endpoint. */
export async function fetchTimeExtent(signal?: AbortSignal): Promise<TimeExtent> {
  const url = `${IMAGE_SERVER_BASE}?f=json`;
  let res: Response;
  try {
    // no-store: obserwowano pojedynczy przypadek zwrotu przestarzałego
    // timeExtent (2 dni wstecz); cache HTTP nie może być tu zmienną.
    res = await fetch(url, { signal, cache: "no-store" });
  } catch (err) {
    throw new RadarApiError(
      `Nie udało się połączyć z serwisem NOAA/NWS: ${(err as Error).message}`
    );
  }
  if (!res.ok) {
    throw new RadarApiError(`Serwis NOAA/NWS zwrócił błąd HTTP ${res.status}`);
  }
  const json = await res.json();
  const extent = json?.timeInfo?.timeExtent;
  if (!Array.isArray(extent) || extent.length !== 2 || typeof extent[0] !== "number") {
    throw new RadarApiError("Odpowiedź serwisu NOAA/NWS nie zawiera poprawnego timeInfo.");
  }
  return { startMs: extent[0], endMs: extent[1] };
}

/**
 * Build the exportImage URL for a single frame at the given observation time
 * (ms epoch UTC). `cacheBusterMs` (pass e.g. Date.now()) appends an inert
 * `_cb` param — used when RETRYING a previously deferred/invalid timestamp so
 * no cache layer (browser or NOAA-side) can pin the earlier bad response to
 * the URL.
 */
export function buildFrameImageUrl(
  frameMs: number,
  bounds: LonLatBounds = CONUS_BOUNDS,
  cacheBusterMs?: number
): string {
  const bbox = boundsToMercatorBbox(bounds);
  const width = EXPORT_IMAGE_WIDTH;
  const aspect = (bbox.xmax - bbox.xmin) / (bbox.ymax - bbox.ymin);
  const height = Math.round(width / aspect);
  const params = new URLSearchParams({
    bbox: `${bbox.xmin},${bbox.ymin},${bbox.xmax},${bbox.ymax}`,
    bboxSR: "3857",
    imageSR: "3857",
    size: `${width},${height}`,
    format: "png32",
    transparent: "true",
    f: "image",
    time: String(Math.round(frameMs))
  });
  if (cacheBusterMs !== undefined) {
    params.set("_cb", String(cacheBusterMs));
  }
  return `${IMAGE_SERVER_BASE}/exportImage?${params.toString()}`;
}

export interface LoadedFrame {
  /** blob: URL to feed into the ImageOverlay; null when the payload was not a decodable image. */
  objectUrl: string | null;
  /** Visible pixels in the downsampled decode; -1 when not a decodable image. */
  pixelCount: number;
  byteSize: number;
}

/**
 * Fetch a frame PNG and return it as an object URL plus content statistics
 * for evaluateCandidate(). `cache: "no-store"` is essential: the service
 * serves an EMPTY transparent PNG for timestamps it advertises in timeInfo
 * but has not rendered yet (~8–12 min lag measured 2026-07-19). A blank
 * cached by the HTTP cache would otherwise stick to that URL for the whole
 * session even after the server catches up. Serving the checked bytes via
 * blob: also guarantees the pixels we validated are exactly the pixels
 * Leaflet renders. A non-image or undecodable payload (ArcGIS can return
 * HTTP 200 with an error body) yields pixelCount -1 and NO object URL — it
 * must never reach the timeline.
 */
export async function loadFrame(url: string, signal?: AbortSignal): Promise<LoadedFrame> {
  let res: Response;
  try {
    res = await fetch(url, { cache: "no-store", signal });
  } catch (err) {
    throw new RadarApiError(`Nie udało się wczytać klatki: ${(err as Error).message}`);
  }
  if (!res.ok) {
    throw new RadarApiError(`Klatka radaru: błąd HTTP ${res.status}`);
  }
  const contentType = res.headers.get("content-type") ?? "";
  const blob = await res.blob();
  if (!contentType.startsWith("image/")) {
    return { objectUrl: null, pixelCount: -1, byteSize: blob.size };
  }
  const pixelCount = await countVisiblePixels(blob);
  if (pixelCount < 0) {
    return { objectUrl: null, pixelCount: -1, byteSize: blob.size };
  }
  return { objectUrl: URL.createObjectURL(blob), pixelCount, byteSize: blob.size };
}

/** Count pixels with non-trivial alpha in a downsampled decode; -1 when undecodable. */
async function countVisiblePixels(blob: Blob): Promise<number> {
  try {
    const bmp = await createImageBitmap(blob);
    const w = 256;
    const h = Math.max(1, Math.round((bmp.height / bmp.width) * w));
    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    if (!ctx) return -1;
    ctx.drawImage(bmp, 0, 0, w, h);
    bmp.close();
    const data = ctx.getImageData(0, 0, w, h).data;
    let count = 0;
    for (let i = 3; i < data.length; i += 4) {
      if (data[i] > 4) count += 1;
    }
    return count;
  } catch {
    return -1;
  }
}
